import { DataFormats } from '@skylab-shared/constants';
import { HttpService, Query, QueryHandler, RootContextBase, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { SaleDocumentKind } from '@skylab-shared/dataModel';
import { MS_PER_MINUTE, StringUtils } from '@skylab-shared/utils';
import { Transform } from 'class-transformer';
import { format } from 'fecha';
import { CountryCollectionQuery } from '../../country/queries/country.query-base';
import { PaymentMethodQuery } from '../../payment-method/queries/payment-method.query-base';

export interface SalesWstoParameters {
    convertToPln: boolean;
    issueDateFrom: Date;
    issueDateTo: Date;
    dueDateFrom: Date;
    dueDateTo: Date;
    saleDateFrom: Date;
    saleDateTo: Date;
    clientGroupId?: string;
    noClientGroup?: boolean;
    kinds: SaleDocumentKind[];
    jpkVatDocumentGroup?: string;
    jpkVatProductGroup?: string;
    omitInvoiceIssuedToReceipt: boolean;
    search: string;
    paymentMethodId: number;
    consumerCountryId: number;
}

export interface SalesWstoModel {
    adjustment: boolean;
    baseCurrencyGross: number;
    baseCurrencyId: string;
    baseCurrencyNet: number;
    baseCurrencyVat: number;
    client: string;
    clientAddress: string | null;
    clientGroup: string;
    clientCountry?: string;
    clientWstoCountry?: string;
    currencyId: string;
    documentId: number;
    documentKind: SaleDocumentKind;
    documentNumber: string;
    gross: number;
    issueDate: Date;
    saleDate: Date;
    dueDate: Date;
    net: number;
    tin: string;
    vat: number;
    paymentMethodName?: string;
    jpkVatGroups?: string | null;
    wstoConsumerCountryId?: number;
}

class SalesWstoInternalItemDTO {
    public readonly adjustment!: boolean;
    public readonly baseCurrencyGross!: number;
    public readonly baseCurrencyId!: string;
    public readonly baseCurrencyNet!: number;
    public readonly baseCurrencyVat!: number;
    public readonly clientAddress!: {
        address: string;
        city: string;
        countryId: number;
        name: string;
        zipCode: string;
    };
    public readonly clientGroup!: string;
    public readonly clientName!: string;
    public readonly currencyId!: string;
    public readonly documentId!: number;
    public readonly documentKind!: SaleDocumentKind;
    public readonly documentNumber!: string;
    public readonly gross!: number;
    public readonly invoiceToReceipt!: boolean;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly issueDate!: Date;
    public readonly jpkVatDocumentGroups!: number;
    public readonly jpkVatProductGroups!: string;
    public readonly kind!: string;
    public readonly net!: number;
    public readonly paymentMethodId!: number;
    public readonly precision!: number;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly saleDate!: Date;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly dueDate!: Date;
    public readonly tin!: string;
    public readonly vat!: number;
    public readonly wstoConsumerCountryId!: number;
}

export class SalesWstoCollectionQuery extends Query<SalesWstoModel[]> {
    public constructor(public readonly parameters: SalesWstoParameters) {
        super();
    }
}

export class SalesWstoCollectionQueryHandlerBase implements QueryHandler<SalesWstoCollectionQuery, SalesWstoModel[]> {
    private static Url = TaggedUrl.join('salesWsto', [Tags.Api]);

    public constructor(
        private readonly http: HttpService,
        private readonly rootContext: RootContextBase
    ) { }

    public async handleAsync(query: SalesWstoCollectionQuery): Promise<SalesWstoModel[]> {
        const { data } = await this.http.get<SalesWstoInternalItemDTO[]>(SalesWstoCollectionQueryHandlerBase.Url, {
            params: {
                ...query.parameters,
                issueDateFrom: query.parameters.issueDateFrom && format(query.parameters.issueDateFrom, DataFormats.date.webApi),
                issueDateTo: query.parameters.issueDateTo && format(query.parameters.issueDateTo, DataFormats.date.webApi),
                dueDateFrom: query.parameters.dueDateFrom && format(query.parameters.dueDateFrom, DataFormats.date.webApi),
                dueDateTo: query.parameters.dueDateTo && format(query.parameters.dueDateTo, DataFormats.date.webApi),
                saleDateFrom: query.parameters.saleDateFrom && format(query.parameters.saleDateFrom, DataFormats.date.webApi),
                saleDateTo: query.parameters.saleDateTo && format(query.parameters.saleDateTo, DataFormats.date.webApi)
            },
            timeout: 3 * MS_PER_MINUTE,
            dto: SalesWstoInternalItemDTO
        });

        const paymentMethods = await this.rootContext.getAsync(PaymentMethodQuery);
        const countries = await this.rootContext.getAsync(CountryCollectionQuery);

        return data.map(item => ({
            adjustment: item.adjustment,
            baseCurrencyId: item.baseCurrencyId,
            baseCurrencyGross: item.baseCurrencyGross,
            baseCurrencyNet: item.baseCurrencyNet,
            baseCurrencyVat: item.baseCurrencyVat,
            client: item.clientName,
            clientAddress: item.clientAddress
                ? StringUtils.join(', ', item.clientAddress.address, item.clientAddress.zipCode, item.clientAddress.city)
                : null,
            clientGroup: item.clientGroup,
            clientCountry: countries.find(elem => elem.id === item.clientAddress?.countryId)?.name,
            clientWstoCountry: countries.find(x => x.id === item.wstoConsumerCountryId)?.name,
            currencyId: item.currencyId,
            documentId: item.documentId,
            documentKind: item.documentKind,
            documentNumber: item.documentNumber,
            gross: item.gross,
            issueDate: item.issueDate,
            dueDate: item.dueDate,
            saleDate: item.saleDate,
            net: item.net,
            tin: item.tin,
            vat: item.vat,
            paymentMethodName: paymentMethods.find(elem => elem.id === item.paymentMethodId)?.name,
            jpkVatGroups: item.jpkVatDocumentGroups ? [item.jpkVatProductGroups, item.jpkVatDocumentGroups].filter(Boolean).join(', ') : item.jpkVatProductGroups || null
        }));
    }
}
